// ── Supabase Auth ──────────────────────────────────────────────────────────
// All auth now goes through Supabase so accounts work across every device.
// ───────────────────────────────────────────────────────────────────────────

import { supabase } from "./supabase";

export interface StoredUser {
  id: string;
  email: string;
  fullName: string;
  username: string;
  avatarDataUrl: string | null;
  createdAt: string;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function sessionUserToStored(user: import("@supabase/supabase-js").User): StoredUser {
  const meta = user.user_metadata ?? {};
  return {
    id: user.id,
    email: user.email ?? "",
    fullName: meta.full_name ?? meta.name ?? "",
    username: meta.username ?? meta.full_name ?? meta.name ?? "",
    avatarDataUrl: meta.avatar_url ?? null,
    createdAt: user.created_at ?? new Date().toISOString(),
  };
}

// ── Public API ───────────────────────────────────────────────────────────────

export async function getCurrentUser(): Promise<StoredUser | null> {
  const { data } = await supabase.auth.getUser();
  if (!data.user) return null;
  return sessionUserToStored(data.user);
}

export function getSessionUserId(): string | null {
  // Synchronous check — reads from in-memory Supabase session
  return null; // kept for compatibility, use getCurrentUser() instead
}

export async function findUserByEmail(email: string): Promise<StoredUser | null> {
  // Supabase handles duplicate email at the auth level — not needed on client
  return null;
}

/** Returns the new user, or null if email already taken / error. */
export async function createUser(
  email: string,
  password: string,
  fullName: string
): Promise<StoredUser | null> {
  const { data, error } = await supabase.auth.signUp({
    email: email.trim().toLowerCase(),
    password,
    options: {
      data: {
        full_name: fullName.trim(),
        username: fullName.trim(),
      },
    },
  });

  if (error || !data.user) return null;
  return sessionUserToStored(data.user);
}

/** Returns user on success, or null on bad credentials. */
export async function loginUser(email: string, password: string): Promise<StoredUser | null> {
  const { data, error } = await supabase.auth.signInWithPassword({
    email: email.trim().toLowerCase(),
    password,
  });
  if (error || !data.user) return null;
  return sessionUserToStored(data.user);
}

export async function logoutUser(): Promise<void> {
  await supabase.auth.signOut();
}

/** Update the currently-logged-in user profile. */
export async function updateCurrentUser(
  updates: Partial<Omit<StoredUser, "id" | "createdAt">>
): Promise<StoredUser | null> {
  const metadata: Record<string, string> = {};
  if (updates.username !== undefined) metadata.username = updates.username;
  if (updates.fullName !== undefined) metadata.full_name = updates.fullName;
  if (updates.avatarDataUrl !== undefined) metadata.avatar_url = updates.avatarDataUrl ?? "";

  const { data, error } = await supabase.auth.updateUser({
    data: metadata,
  });
  if (error || !data.user) return null;
  return sessionUserToStored(data.user);
}

/** Send a password reset email. */
export async function sendPasswordResetEmail(email: string): Promise<{ error: string | null }> {
  const redirectTo = `${window.location.origin}/reset-password`;
  const { error } = await supabase.auth.resetPasswordForEmail(email.trim().toLowerCase(), {
    redirectTo,
  });
  if (error) return { error: error.message };
  return { error: null };
}

/** Update password directly (used on the reset-password page after recovery link). */
export async function updatePassword(newPassword: string): Promise<{ error: string | null }> {
  const { error } = await supabase.auth.updateUser({ password: newPassword });
  if (error) return { error: error.message };
  return { error: null };
}

export function subscribeToAuthChanges(cb: () => void): () => void {
  const { data } = supabase.auth.onAuthStateChange(() => {
    cb();
  });
  return () => data.subscription.unsubscribe();
}

// Legacy no-ops kept to avoid import errors in other files
export async function getUsers(): Promise<StoredUser[]> {
  return [];
}

